/**
 * Form dropdown options for Order Hub forms
 * These options are used across Order Management forms
 */

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

/**
 * i18n Configuration
 * List of constant variables that should be included for translation.
 * Brand names, product names, and identifiers should NOT be included.
 */
export const I18N_TRANSLATABLE_CONSTANTS = [
  'titleOptions',
  'customerTypeOptions',
  'registrationProcessOptions',
  'orderStatusOptions',
  'deliveryStatusOptions',
] as const;

export { getTitleOptions } from '../../constants/constants';

/**
 * Customer type options
 * @param labels - i18n labels from Contentful
 */
export const getCustomerTypeOptions = (labels?: Labels) => [
  { label: labels?.['const.customerTypeOptions.private'] ?? 'Private', value: 'Private' },
  { label: labels?.['const.customerTypeOptions.business'] ?? 'Business', value: 'Business' },
];

/**
 * Registration process options
 * @param labels - i18n labels from Contentful
 * @param countryCode - user country code used to restrict market-specific options
 */
export const getRegistrationProcessOptions = (labels?: Labels, countryCode?: string) => {
  const selfRegistrationOption = {
    label: labels?.['const.registrationProcessOptions.selfRegistration'] ?? 'Self Registration',
    value: 'Self-Registration',
  };

  if (countryCode?.trim().toUpperCase() === 'NL') {
    return [selfRegistrationOption];
  }

  return [
    {
      label: labels?.['const.registrationProcessOptions.lucidRegistration'] ?? 'Lucid Registration',
      value: 'Lucid Registration',
    },
    selfRegistrationOption,
  ];
};

/**
 * Order status options
 * @param labels - i18n labels from Contentful
 */
export const getOrderStatusOptions = (labels?: Labels) => [
  { label: labels?.['const.orderStatusOptions.pending'] ?? 'Pending', value: 'Pending' },
  { label: labels?.['const.orderStatusOptions.confirmed'] ?? 'Confirmed', value: 'Confirmed' },
  {
    label: labels?.['const.orderStatusOptions.inProduction'] ?? 'In Production',
    value: 'InProduction',
  },
  {
    label: labels?.['const.orderStatusOptions.readyForDelivery'] ?? 'Ready for Delivery',
    value: 'ReadyForDelivery',
  },
  { label: labels?.['const.orderStatusOptions.delivered'] ?? 'Delivered', value: 'Delivered' },
  { label: labels?.['const.orderStatusOptions.cancelled'] ?? 'Cancelled', value: 'Cancelled' },
];

/**
 * Delivery status options
 * @param labels - i18n labels from Contentful
 */
export const getDeliveryStatusOptions = (labels?: Labels) => [
  { label: labels?.['const.deliveryStatusOptions.scheduled'] ?? 'Scheduled', value: 'scheduled' },
  { label: labels?.['const.deliveryStatusOptions.inTransit'] ?? 'In Transit', value: 'in_transit' },
  { label: labels?.['const.deliveryStatusOptions.delivered'] ?? 'Delivered', value: 'delivered' },
  { label: labels?.['const.deliveryStatusOptions.delayed'] ?? 'Delayed', value: 'delayed' },
];

/**
 * Document category options
 * @param labels - i18n labels from Contentful
 */
export const getDocumentCategoryOptions = (labels?: Labels) => [
  {
    label: labels?.['const.documentCategoryOptions.driversLicense'] ?? "Driver's License",
    value: 'drivers_license',
  },
  {
    label: labels?.['const.documentCategoryOptions.proofOfAddress'] ?? 'Proof of Address',
    value: 'proof_of_address',
  },
  { label: labels?.['const.documentCategoryOptions.insurance'] ?? 'Insurance', value: 'insurance' },
  {
    label: labels?.['const.documentCategoryOptions.registration'] ?? 'Registration',
    value: 'registration',
  },
  { label: labels?.['const.documentCategoryOptions.contract'] ?? 'Contract', value: 'contract' },
  { label: labels?.['const.documentCategoryOptions.invoice'] ?? 'Invoice', value: 'invoice' },
];

/**
 * Payment method options
 * @param labels - i18n labels from Contentful
 */
export const getPaymentMethodOptions = (labels?: Labels) => [
  { label: labels?.['const.paymentMethodOptions.cash'] ?? 'Cash', value: 'cash' },
  { label: labels?.['const.paymentMethodOptions.lease'] ?? 'Lease', value: 'lease' },
  { label: labels?.['const.paymentMethodOptions.finance'] ?? 'Finance', value: 'finance' },
  {
    label: labels?.['const.paymentMethodOptions.creditCard'] ?? 'Credit Card',
    value: 'credit_card',
  },
  { label: labels?.['const.paymentMethodOptions.ach'] ?? 'ACH', value: 'ach' },
  {
    label: labels?.['const.paymentMethodOptions.cryptocurrency'] ?? 'Cryptocurrency',
    value: 'cryptocurrency',
  },
];

/**
 * Country code options for address fields
 * @param labels - i18n labels from Contentful
 */
export const getCountryOptions = (labels?: Labels) => [
  { label: labels?.['const.countryOptions.de'] ?? 'Germany (DE)', value: 'DE' },
  { label: labels?.['const.countryOptions.us'] ?? 'United States (US)', value: 'US' },
  { label: labels?.['const.countryOptions.ca'] ?? 'Canada (CA)', value: 'CA' },
  { label: labels?.['const.countryOptions.uk'] ?? 'United Kingdom (UK)', value: 'UK' },
  { label: labels?.['const.countryOptions.fr'] ?? 'France (FR)', value: 'FR' },
  { label: labels?.['const.countryOptions.it'] ?? 'Italy (IT)', value: 'IT' },
  { label: labels?.['const.countryOptions.es'] ?? 'Spain (ES)', value: 'ES' },
  { label: labels?.['const.countryOptions.nl'] ?? 'Netherlands (NL)', value: 'NL' },
  { label: labels?.['const.countryOptions.ch'] ?? 'Switzerland (CH)', value: 'CH' },
  { label: labels?.['const.countryOptions.at'] ?? 'Austria (AT)', value: 'AT' },
];

/**
 * Returns the Badge color for a line item status string.
 * Statuses: In Review, Waiting for Confirmation, Confirmed, Ready for Dispatch, Fulfilled, Denied / Canceled.
 */
export const getLineItemStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'in review':
      return 'pink';
    case 'waiting for confirmation':
      return 'orange';
    case 'confirmed':
      return 'indigo';
    case 'ready for dispatch':
      return 'blue';
    case 'fulfilled':
      return 'green';
    case 'denied / canceled':
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Returns the Badge color for an importer order status string.
 * Statuses: Initiated, Partially Filled, Fulfilled, Canceled.
 */
export const getImporterOrderStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'initiated':
      return 'indigo';
    case 'partially_fulfilled':
      return 'orange';
    case 'fulfilled':
      return 'green';
    case 'canceled':
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Returns the Badge color for a given order status string.
 * Shared across Order Desk and Manage Deliveries tables.
 */
export const getOrderStatusBadgeColor = (status: string) => {
  if (!status) return 'gray';

  switch (status.toLowerCase()) {
    case 'pending':
      return 'yellow';
    case 'in progress':
      return 'blue';
    case 'waiting for confirmation':
      return 'orange';
    case 'confirmed':
      return 'purple';
    case 'ready for dispatch':
    case 'ready for delivery':
      return 'teal';
    case 'in review':
      return 'pink';
    case 'fulfilled':
      return 'green';
    default:
      return 'gray';
  }
};

/**
 * Maps an orderPreference enum value (CUSTOMER, DEMO, INVENTORY, PRESS,
 * SERVICE_LOANER) to its user-facing "Order Type" label.
 * Shared across the Order Desk line-items table and vehicle order details.
 * @param value - The orderPreference enum value from the vehicle order.
 * @param labels - i18n labels from Contentful.
 * @returns The localized label, or undefined when no value is provided.
 */
export const getOrderPreferenceLabel = (
  value: string | null | undefined,
  labels?: Labels
): string | undefined => {
  if (!value) return undefined;
  const map: Record<string, string> = {
    CUSTOMER: labels?.['details.orderType.customer'] ?? 'Customer',
    DEMO: labels?.['details.orderType.demo'] ?? 'Demo',
    INVENTORY: labels?.['details.orderType.inventory'] ?? 'Inventory',
    PRESS: labels?.['details.orderType.press'] ?? 'Press',
    SERVICE_LOANER: labels?.['details.orderType.serviceLoaner'] ?? 'Service Loaner',
  };
  return map[value] ?? value;
};
